import { useEffect, useState } from 'react'
import axios from 'axios'

import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'

import { ChatSearchRequestError, searchChats } from '../api/chat-search-api'
import { isSearchableQuery } from './query'
import { ChatSearchHit } from './types'

export function useChatSearch(token?: string) {
	const { showMessage } = useShowDataStore()
	const [query, setQuery] = useState('')
	const [debouncedQuery, setDebouncedQuery] = useState('')
	const [results, setResults] = useState<ChatSearchHit[]>([])
	const [loading, setLoading] = useState(false)
	const [searched, setSearched] = useState(false)
	const [unavailable, setUnavailable] = useState(false)

	useEffect(() => {
		const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300)
		return () => window.clearTimeout(timer)
	}, [query])

	useEffect(() => {
		if (!isSearchableQuery(debouncedQuery)) {
			setResults([])
			setLoading(false)
			setSearched(false)
			setUnavailable(false)
			return
		}

		const controller = new AbortController()
		setLoading(true)
		setUnavailable(false)

		searchChats({ q: debouncedQuery, token, signal: controller.signal })
			.then((data) => {
				setResults(data.results ?? [])
				setSearched(true)
			})
			.catch((error: unknown) => {
				if (axios.isCancel(error)) {
					return
				}
				setResults([])
				setSearched(true)
				if (error instanceof ChatSearchRequestError && error.status === 503) {
					setUnavailable(true)
					showMessage('Поиск временно недоступен. Повторите запрос')
					return
				}
				if (error instanceof ChatSearchRequestError && error.status === 422) {
					showMessage('Некорректный запрос поиска')
					return
				}
				showMessage('Не удалось выполнить поиск')
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setLoading(false)
				}
			})

		return () => controller.abort()
	}, [debouncedQuery, showMessage, token])

	return {
		query,
		setQuery,
		debouncedQuery,
		results,
		loading,
		searched,
		unavailable,
		searchable: isSearchableQuery(debouncedQuery),
	}
}
